import { useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ISuperAdminReportView } from '../../../../shared/services/super-admin/super-admin.service';
import { UsageDelta } from './usage-ui';

export interface BreakdownItem { label: string; count: number; }

const REPORT_TYPE_LABELS: { [key: string]: string } = {
  SECR: 'SECR', GHG: 'GHG', ESRS: 'ESRS', BCORP: 'B Corp', WEBPAGE: 'Webpage', ANALYTICS: 'Analytics',
};

// A single row of the report-views breakdown. Analytics views carry a report id/name, so
// they read "Analytics - <report name>" rather than being lumped into one Analytics bucket.
export const reportViewLabel = (row: ISuperAdminReportView): string =>
  row.reportType === 'ANALYTICS'
    ? `Analytics - ${row.name || 'Unnamed report'}`
    : (REPORT_TYPE_LABELS[row.reportType] || row.reportType);

export const reportBreakdownItems = (rows: ISuperAdminReportView[]): BreakdownItem[] =>
  rows.map(r => ({ label: reportViewLabel(r), count: r.count }));

// The built-in analytics dashboard's tab keys, mapped to their display names.
const ANALYTICS_TAB_LABELS: { [key: string]: string } = {
  overview: 'Overview',
  energy: 'Energy',
  group: 'Emissions by Group',
  subsidiary: 'Emissions by Subsidiary',
  location: 'Emissions by Location',
  supplier: 'Emissions by Supplier',
  project: 'Emissions by Project',
  surveys: 'Surveys',
  flag: 'FLAG',
};

export const analyticsTabBreakdownItems = (rows: { tab: string; count: number }[]): BreakdownItem[] =>
  rows.map(r => ({ label: ANALYTICS_TAB_LABELS[r.tab] || r.tab, count: r.count }));

// SSO provider values are stored uppercase (e.g. GOOGLE / ENTRA), so match case-insensitively.
const SSO_PROVIDER_LABELS: { [key: string]: string } = {
  google: 'Google',
  entra: 'Entra',
  microsoft: 'Entra',
  azure: 'Entra',
};

export const ssoProviderBreakdownItems = (
  rows: { eventType: string; provider: string; count: number }[],
  eventType: string,
): BreakdownItem[] =>
  rows
    .filter(r => r.eventType === eventType)
    .map(r => ({ label: SSO_PROVIDER_LABELS[(r.provider || '').toLowerCase()] || r.provider, count: r.count }));

const listRowSx = { display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' } as const;

// Events-by-type list where rows that have a breakdown (e.g. "Reports viewed", "Analytics
// dashboard viewed") are clickable and expand to show it. Used on both the platform
// Functionality tab and the per-organisation page.
export const UsageEventsByType = ({
  events,
  breakdowns,
  labels,
}: {
  events: { eventType: string; count: number; previous?: number }[];
  breakdowns: { [eventType: string]: BreakdownItem[] };
  labels: { [key: string]: string };
}) => {
  const [openKeys, setOpenKeys] = useState<{ [key: string]: boolean }>({});

  if (events.length === 0) {
    return <Typography variant="body2" sx={{ color: 'text.secondary' }}>Nothing recorded in this window.</Typography>;
  }

  return (
    <>
      {events.map(row => {
        const breakdown = breakdowns[row.eventType] || [];
        const expandable = breakdown.length > 0;
        const open = !!openKeys[row.eventType];
        return (
          <Box key={row.eventType}>
            <Box
              onClick={expandable ? () => setOpenKeys(prev => ({ ...prev, [row.eventType]: !prev[row.eventType] })) : undefined}
              sx={{ ...listRowSx, alignItems: 'center', cursor: expandable ? 'pointer' : 'default' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                {expandable && (open
                  ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />)}
                <Typography variant="body2" sx={{ color: 'text.primary' }}>{labels[row.eventType] || row.eventType}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>{row.count}</Typography>
                <UsageDelta current={row.count} previous={row.previous} />
              </Box>
            </Box>
            {expandable && (
              <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 3.5, py: 0.5 }}>
                  {breakdown.map((b, idx) => (
                    <Box key={`${b.label}-${idx}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.label}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', flexShrink: 0 }}>{b.count}</Typography>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            )}
          </Box>
        );
      })}
    </>
  );
};
